import { PageLoadingFallback } from 'api/page-loading-fallback';
import {
  isPermitted,
  Logo,
  Button,
  stickyHeightRef,
  useListIntegrations,
} from '@plerionhq/design-system';
import { identifyUser } from '@plerionhq/design-system/analytics';
import { AwsRum } from 'aws-rum-web';
import dayjs from 'dayjs';
import React, { Suspense, useEffect, useRef } from 'react';
import { LoaderIcon, toast } from 'react-hot-toast';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { isFirstLoad } from 'api/query-state';
import { Features, useFeatureFlags } from '../../api/featureFlag';
import { useGetG2ReviewAccessToken } from '../../api/g2-review';
import {
  useTenantHasCloudProviderIntegrated,
  useTenantHasIntegrationsWithEnvironmentSuggestions,
} from '../../api/integration';
import { useOrganizationInfo } from '../../api/organization';
import { useUserProfile } from '../../api/profile';
import { useDefaultTenant } from '../../api/tenants';
import { isG2ReviewEnabled } from '../../constants/featureFlags';
import { restrictedSideBarUrl } from '../../constants/restrictedUrl';
import { OAUTH_CONSENT_PATH } from '../../constants/oauthConsentPath';
import { withErrorBoundary } from '../../hoc/withErrorBoundary';
import { PleriLoaderPreview } from '../../pages/PleriLoaderPreview';
import { EnvironmentsPage } from '../../pages/EnvironmentsPage';
import { DisplayOrganizationDeleteInProgressBlock } from '../DisplayOrganizationDeleteInProgressBlock/DisplayOrganizationDeleteInProgressBlock';
import { DisplayUsageBlock } from '../DisplayUsageBlock/DisplayUsageBlock';
import { NavBar } from '../NavBar/NavBar';
import { ResourceCenterNavBar } from '../NavBar/ResourceCenterNavBar';
import { PricingCalculator } from '../PricingCalculator/PricingCalculator';
import { Progress } from '../Progress/Progress';
import { ProtectedComponent } from '../shared/ProtectedComponent/ProtectedComponent';
import { SideNav } from '../SideNav/SideNav';
import { EnvironmentPanel } from '../temporary-notices/EnvironmentPanel/EnvironmentPanel';
import { EnvironmentSuggestionPanel } from '../temporary-notices/EnvironmentPanel/EnvironmentSuggestionPanel';
import { hideSideNav } from '../utils/hideSideNav';
import { useLocalStorageQuery } from '../utils/manageCustomReportNavItems';
import { TenantRoutes } from './TenantRoutes/TenantRoutes';
import { FlappyLeaderboard } from 'authInterface/FlappyLeaderboard';

import { useTranslation } from 'react-i18next';

const SettingsInterface = withErrorBoundary(
  React.lazy(() => import('settings/Settings')),
);

const CustomReportBuilderInterface = withErrorBoundary(
  React.lazy(() => import('customReportBuilder/CustomReportBuilder')),
);
const TenantInterface = withErrorBoundary(
  React.lazy(() => import('tenant/tenant')),
  () => <></>,
);

const SearchInterface = withErrorBoundary(
  React.lazy(() => import('search/search')),
);

const PleriSpotlight = withErrorBoundary(
  React.lazy(
    () =>
      import('../../../../search/src/components/Pleri/mf-exports/PleriSpotlight'),
  ),
  () => <></>,
);

interface AuthenticatedRoutesProps {
  awsRumInstance: AwsRum | null;
}

const UserGuide = withErrorBoundary(
  React.lazy(() => import('../../micro-frontends/components/UserGuide')),
  () => <></>,
);

const QuickStart = withErrorBoundary(
  React.lazy(() => import('../../micro-frontends/components/QuickStart')),
);

interface ReviewPanelProps {
  onDismiss: () => void;
  reviewUrl: string;
}

const ReviewPanel = ({ onDismiss, reviewUrl }: ReviewPanelProps) => {
  const { t } = useTranslation('containerInterface');

  return (
    <>
      <div className="flex flex-row items-center">
        <Logo name="G2" />
        <span className="pl-4 text-lg">{t('likeUsLoveUsLeavePlerionA')}</span>
      </div>
      <span className="flex gap-x-2">
        <Button
          label={t('dismiss')}
          size="small"
          variant="secondary"
          onClick={onDismiss}
        />
        <Button
          label={t('reviewUs')}
          variant="primary"
          size="small"
          onClick={() => {
            window.open(
              reviewUrl,
              '_blank',
              'width=600,height=700,left=200,top=100,scrollbars=yes,resizable=yes',
            );
          }}
        />
      </span>
    </>
  );
};

export function AuthenticatedRoutes({
  awsRumInstance,
}: AuthenticatedRoutesProps) {
  const { t } = useTranslation('containerInterface');
  const location = useLocation();
  const navigate = useNavigate();

  const { data: userData } = useUserProfile();
  const featureFlagsQuery = useFeatureFlags();
  const featureFlags = featureFlagsQuery.data;
  const { data: defaultTenantId } = useDefaultTenant();
  const integrationsQuery = useListIntegrations();
  const integrationsData = integrationsQuery.data;
  const isTenantIntegratedWithCloudProvider =
    useTenantHasCloudProviderIntegrated();
  const hasIntegrationsWithEnvironmentSuggestions =
    useTenantHasIntegrationsWithEnvironmentSuggestions();
  const g2ReviewEnabled = isG2ReviewEnabled();
  const g2ReviewAccessToken = useGetG2ReviewAccessToken({
    enabled: g2ReviewEnabled,
  });
  const localStorageKey = `${defaultTenantId}:${userData?.id}`;
  const { data: storedNavConfig, isLoading: reportNavItemLoading } =
    useLocalStorageQuery(localStorageKey);
  const { data: orgData } = useOrganizationInfo();
  const [isUserReviewDeclined, setIsUserReviewDeclined] = React.useState(
    localStorage.getItem('plerion.g2ReviewDeclined'),
  );
  const [isEnvironmentPanelDismissed, setIsEnvironmentPanelDismissed] =
    React.useState(localStorage.getItem('plerion.environmentPanelDismissed'));
  const hasIdentified = useRef(false);

  const shouldHideShell =
    new URLSearchParams(location.search).get('hideShell') === 'true';
  // Rendered by the auth interface; the shell stays out of the way.
  const isOAuthConsentPage = location.pathname.startsWith(OAUTH_CONSENT_PATH);
  const hideNavigation =
    location.pathname.startsWith('/onboarding') ||
    isOAuthConsentPage ||
    shouldHideShell;

  const hideSideNavbar =
    hideSideNav(
      location.pathname,
      hideNavigation
        ? [...restrictedSideBarUrl, '/onboarding', OAUTH_CONSENT_PATH]
        : restrictedSideBarUrl,
    ) || shouldHideShell;

  useEffect(() => {
    awsRumInstance?.recordPageView(location.pathname);
  }, [location.pathname, awsRumInstance]);

  // redirect to getting started page if user is new
  useEffect(() => {
    // skip redirecting for these urls
    if (
      location.pathname.startsWith('/profile') ||
      location.pathname === '/sign-out'
    ) {
      return;
    }
  });

  // The list answered and is empty. A failed list is not "no integrations":
  // sending an admin to onboarding on a failed request would be wrong.
  const hasIntegrations = integrationsData && integrationsData.length > 0;
  const integrationsListLoaded = integrationsData !== undefined;
  const isQuickStartEnabled =
    featureFlags && featureFlags.includes(Features.QuickStart);
  // Hold the shell only for a query's first attempt; after one failure it
  // renders with whatever it has.
  const isLoadingData =
    isFirstLoad(integrationsQuery) || isFirstLoad(featureFlagsQuery);
  const isHomePage = location.pathname === '/';
  const isMarketplaceLinking = location.pathname.includes(
    '/admin/marketplace/link',
  );

  // Redirect to onboarding if the customer hasn't added any integrations
  useEffect(() => {
    const shouldRedirectToOnboarding =
      integrationsListLoaded &&
      !hasIntegrations &&
      isHomePage &&
      isQuickStartEnabled &&
      !isMarketplaceLinking &&
      userData &&
      isPermitted(userData?.permissions || [], [
        'organization:Administration',
        'tenant:Administration',
      ]);

    if (shouldRedirectToOnboarding) {
      navigate('/onboarding');
    }
  }, [
    integrationsListLoaded,
    hasIntegrations,
    isQuickStartEnabled,
    isHomePage,
    userData,
    navigate,
    isMarketplaceLinking,
  ]);

  useEffect(() => {
    if (location.pathname === '/') {
      const value = window.atob(
        sessionStorage.getItem('marketplaceToken') || '',
      );
      if (value && value !== '') {
        navigate('/admin/marketplace/link');
      }
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (location.pathname.startsWith('/sign-up/marketplace')) {
      const search = new URLSearchParams(location.search);
      let marketplaceToken = '';

      try {
        marketplaceToken = window.atob(search.get('marketplaceToken') || '');
        sessionStorage.setItem(
          'marketplaceToken',
          window.btoa(marketplaceToken),
        );
        navigate('/admin/marketplace/link');
      } catch {
        toast.error(t('invalidMarketplaceToken'));
      }
    }
  }, [navigate, location.pathname, location.search, t]);

  useEffect(() => {
    if (userData && orgData && !!awsRumInstance) {
      awsRumInstance?.addSessionAttributes({
        userEmail: userData.email,
        userName: userData.name as string,
        orgName: orgData.profile.name,
        orgType: orgData.profile.type,
      });
    }
  }, [awsRumInstance, userData, orgData]);

  useEffect(() => {
    if (userData && orgData && !hasIdentified.current) {
      hasIdentified.current = true;
      identifyUser({
        userId: userData.id,
        properties: {
          email: userData.email,
          name: userData.name,
          organization_id: userData.organizationId,
        },
      });
    }
  }, [userData, orgData]);

  if (
    location.pathname === '/leaderboard' &&
    localStorage.getItem('plerion.enableFlappyLeaderboard') === 'true'
  ) {
    return <FlappyLeaderboard />;
  }

  if (isLoadingData) {
    return (
      <div className="flex h-full min-h-screen flex-row items-center justify-center gap-2">
        <LoaderIcon className="h-4 w-4" />
      </div>
    );
  }

  const isUserAllowedToReview = isAtLeast90DaysOld(userData?.createdAt);
  const isUserOnHomeDashboard = location.pathname === '/';
  const isReviewPanelVisible =
    g2ReviewEnabled &&
    isUserAllowedToReview &&
    isTenantIntegratedWithCloudProvider &&
    !isUserReviewDeclined &&
    !g2ReviewAccessToken.data?.reviewExists &&
    g2ReviewAccessToken.data?.accessToken &&
    isUserOnHomeDashboard;

  const handleDismissReviewPanel = () => {
    localStorage.setItem('plerion.g2ReviewDeclined', 'true');
    setIsUserReviewDeclined('true');
  };

  const reviewUrl =
    g2ReviewAccessToken.data?.accessToken != null
      ? `https://www.g2.com/partnerships/Plerion/users/login.embed?state=${g2ReviewAccessToken.data.accessToken}&email=${userData?.email ?? ''}`
      : '';

  const handleDismissEnvironmentPanel = () => {
    localStorage.setItem('plerion.environmentPanelDismissed', 'true');
    setIsEnvironmentPanelDismissed('true');
  };

  return (
    <>
      {location.pathname.startsWith('/resources/cloud-knowledge-base') ? (
        <ResourceCenterNavBar />
      ) : (
        !hideNavigation && <NavBar orgData={orgData} />
      )}
      <Suspense fallback={<Progress />}>
        <UserGuide />
      </Suspense>
      <div
        className={`flex h-screen ${hideNavigation ? '' : 'max-h-[calc(100vh-48px)]'} w-full items-stretch`}
      >
        {!hideSideNavbar && !reportNavItemLoading && (
          <SideNav reportNavItems={storedNavConfig || []} />
        )}
        <Suspense fallback={<PageLoadingFallback legacy={<Progress />} />}>
          <div
            className={`flex h-screen ${hideNavigation ? '' : 'max-h-[calc(100vh-48px)]'} w-full flex-grow items-stretch overflow-auto`}
            id="container-interface-parent-container"
          >
            <div className="flex w-full flex-grow flex-col items-stretch overflow-auto">
              <div
                ref={stickyHeightRef('--app-sticky-top')}
                className="sticky top-0 z-[98] w-full self-start bg-gray-100 dark:bg-gray-900"
              >
                <DisplayOrganizationDeleteInProgressBlock />
                <DisplayUsageBlock />
                <PleriSpotlight />
                {isReviewPanelVisible && (
                  <div
                    className={`${location.pathname.startsWith('/admin/') ? 'm-4' : 'mx-4 mt-4'} rounded-panel border-container-border flex items-center justify-between gap-4 border bg-white p-4 dark:bg-gray-800`}
                  >
                    <ReviewPanel
                      onDismiss={handleDismissReviewPanel}
                      reviewUrl={reviewUrl}
                    />
                  </div>
                )}
                {!location.pathname.startsWith('/environments') &&
                  !isEnvironmentPanelDismissed && (
                    <ProtectedComponent
                      permission={[
                        'organization:Administration',
                        'tenant:Administration',
                      ]}
                    >
                      {hasIntegrationsWithEnvironmentSuggestions ? (
                        <EnvironmentSuggestionPanel
                          onDismiss={handleDismissEnvironmentPanel}
                        />
                      ) : isTenantIntegratedWithCloudProvider ? (
                        <EnvironmentPanel
                          onDismiss={handleDismissEnvironmentPanel}
                        />
                      ) : null}
                    </ProtectedComponent>
                  )}
              </div>
              <div className="flex-grow">
                <QuickStart />
                <Routes>
                  <Route
                    path="/preview/pleri-loader"
                    element={<PleriLoaderPreview />}
                  />
                  <Route path={'/sign-out'} />
                  <Route path={'/auth'} />
                  <Route path={OAUTH_CONSENT_PATH} />
                  <Route
                    path={'/environments'}
                    element={<EnvironmentsPage />}
                  />
                  <Route path={'/pricing'} element={<PricingCalculator />} />
                  <Route
                    path="/settings/custom-reports/*"
                    element={<CustomReportBuilderInterface />}
                  />
                  <Route path="/settings/pleri" element={<SearchInterface />} />
                  <Route
                    path="/admin/*"
                    element={
                      <div className="flex h-screen max-h-[calc(100vh-48px)] flex-col overflow-auto">
                        <SettingsInterface />
                      </div>
                    }
                  />
                  <Route
                    path="/super-admin/*"
                    element={
                      <div className="flex h-screen max-h-[calc(100vh-48px)] flex-col overflow-auto">
                        <SettingsInterface />
                      </div>
                    }
                  />
                  <Route
                    path="/profile/*"
                    element={
                      <div className="flex h-screen max-h-[calc(100vh-48px)] flex-col overflow-auto">
                        <SettingsInterface />
                      </div>
                    }
                  />
                  <Route path="/settings/*" element={<TenantInterface />} />
                  <Route
                    path="/onboarding/*"
                    element={
                      <div className="flex h-screen flex-col overflow-auto">
                        <TenantInterface />
                      </div>
                    }
                  />
                  <Route path="/*" element={<TenantRoutes />} />
                  <Route
                    element={
                      <div className="flex h-full items-center justify-center pb-20 text-2xl leading-tight font-medium text-gray-500 dark:text-gray-400">
                        {t('text404NotFound')}
                      </div>
                    }
                  />
                </Routes>
              </div>
            </div>
          </div>
        </Suspense>
      </div>
    </>
  );
}

/**
 * Check if the given date is 90 days before now.
 * @param date - The date to compare.
 * @returns true if the date is at least 90 days before now, false otherwise.
 */
const isAtLeast90DaysOld = (date?: string | Date): boolean => {
  if (!dayjs(date).isValid()) {
    return false;
  }
  const targetDate = dayjs(date);
  const ninetyDaysAgo = dayjs().subtract(90, 'day');
  return targetDate.isBefore(ninetyDaysAgo, 'day'); // Use 'day' to ignore time-of-day precision
};
